import { useMemo, useState } from 'react';
import { Plus, Trash2, ExternalLink, Pencil, Linkedin, Github, Mail } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/Avatar';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAdminTeam, useUpsertTeamMember, useDeleteTeamMember, type TeamRow } from '@/lib/admin-queries';
import { TeamEditForm } from '@/features/team/TeamEditForm';
import { toast } from '@/components/ui/Toast';

const EMPTY: TeamRow = {
  slug: '',
  name: '',
  role: '',
  departments: [],
  bio: '',
  bio_md: '',
  skills: [],
  portrait_media_id: null,
  linkedin_url: '',
  github_url: '',
  email: '',
  display_order: 0,
  is_active: true,
  status: 'published',
};

export function TeamPage() {
  const { data: rows = [], isLoading } = useAdminTeam();
  const upsert = useUpsertTeamMember();
  const remove = useDeleteTeamMember();
  const [editing, setEditing] = useState<TeamRow | null>(null);
  const [deleting, setDeleting] = useState<TeamRow | null>(null);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Member',
        cell: (r: TeamRow) => {
          const initials = r.name
            .split(/\s+/)
            .map((p) => p[0])
            .filter(Boolean)
            .slice(0, 2)
            .join('')
            .toUpperCase();
          return (
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                {r.portrait_media_id && (
                  <AvatarImage
                    src={`${(import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? ''}/storage/v1/object/public/public/${r.portrait_media_id}`}
                    alt={r.name}
                  />
                )}
                <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{r.name}</p>
                <p className="text-xs text-fg-subtle">{r.role}</p>
              </div>
            </div>
          );
        },
      },
      {
        key: 'departments',
        header: 'Departments',
        cell: (r: TeamRow) => (
          <div className="flex flex-wrap gap-1">
            {r.departments.length === 0 ? (
              <span className="text-xs text-fg-subtle">—</span>
            ) : (
              r.departments.map((d) => (
                <Badge key={d} variant="gold">{d}</Badge>
              ))
            )}
          </div>
        ),
      },
      {
        key: 'skills',
        header: 'Skills',
        cell: (r: TeamRow) => (
          <div className="flex max-w-[200px] flex-wrap gap-1">
            {r.skills.slice(0, 3).map((s) => (
              <span key={s} className="rounded-md bg-bg-subtle px-2 py-0.5 text-[10px] text-fg-muted">
                {s}
              </span>
            ))}
            {r.skills.length > 3 && <span className="text-[10px] text-fg-subtle">+{r.skills.length - 3}</span>}
          </div>
        ),
      },
      {
        key: 'socials',
        header: 'Socials',
        cell: (r: TeamRow) => (
          <div className="flex items-center gap-1.5 text-fg-muted">
            {r.linkedin_url && <Linkedin className="h-3.5 w-3.5" />}
            {r.github_url && <Github className="h-3.5 w-3.5" />}
            {r.email && <Mail className="h-3.5 w-3.5" />}
            {!r.linkedin_url && !r.github_url && !r.email && <span className="text-xs">—</span>}
          </div>
        ),
      },
      {
        key: 'active',
        header: 'Active',
        cell: (r: TeamRow) => (r.is_active ? <Badge variant="success">Active</Badge> : <Badge>Hidden</Badge>),
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: TeamRow) => (
          <div className="flex items-center justify-end gap-1">
            {r.status === 'published' && (
              <a
                href={`https://vonlof.co/team`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View team page"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  async function onSave(row: TeamRow) {
    try {
      await upsert.mutateAsync(row);
      toast.success(row.id ? 'Member updated' : 'Member added');
      setEditing(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Member removed');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Team</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Members</h1>
          <p className="mt-2 text-sm text-fg-muted">Manage team profiles, portraits, and skills.</p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing({ ...EMPTY })}>
          New member
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No team members yet. Click New member to add one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.id ? 'Edit team member' : 'New team member'}
        size="xl"
      >
        {editing && (
          <TeamEditForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => setEditing(null)}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Remove this team member?"
        description={deleting ? `"${deleting.name}" will be removed from the team page.` : ''}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}